<template>
  <div class="transaction-history-container">
    <div class="page-header">
      <h2>{{ $t('İşlem Geçmişi') }}</h2>
      <div class="header-actions">
        <div class="items-per-page-selector">
          <span>{{ $t('profile.perpage') }}:</span>
          <select v-model="itemsPerPage" @change="currentPage = 1">
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Filtreleme Butonları -->
    <div class="filter-section">
      <div class="filter-buttons">
        <button
          :class="{ active: activeFilter === 'deposit' }"
          @click="setFilter('deposit')"
          class="filter-btn"
        >
          <span class="btn-icon">↓</span>
          {{ $t('profile.Deposit') }}
        </button>
        <button
          :class="{ active: activeFilter === 'withdrawal' }"
          @click="setFilter('withdrawal')"
          class="filter-btn"
        >
          <span class="btn-icon">↑</span>
          {{ $t('profile.Withdrawal') }}
        </button>
      </div>
      
      <div class="results-info" v-if="!loading">
        <span class="results-count">{{ filteredTransactions.length }} {{ $t('profile.result found') }}</span>
      </div>
    </div>

    <!-- Yükleniyor Durumu -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>{{ $t('profile.loading...') }}</p>
    </div>

    <!-- Veri Yoksa -->
    <div v-else-if="filteredTransactions.length === 0" class="empty-state">
      <div class="empty-icon">📋</div>
      <h3>{{ $t('profile.There are no transactions yet') }}</h3>
      <p>{{ $t('profile.No action found matching this filter.') }}</p>
    </div>

 <!-- Masaüstü Tablo -->
<div v-if="!loading && filteredTransactions.length > 0" class="table-container desktop-table">
  <table class="transaction-table">
    <thead>
      <tr>
        <th>{{ $t('profile.Transaction Type') }}</th>
        <th>{{ $t('profile.Amount') }}</th>
        <th>{{ $t('profile.Situation') }}</th>
        <th>{{ $t('profile.Date') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(transaction, index) in paginatedTransactions" :key="index">
        <td>
          <div class="transaction-type">
            <span class="type-icon" :class="transaction.type">
              {{ transaction.type === 'deposit' ? '↓' : '↑' }}
            </span>
            {{ transaction.type === 'deposit' ? $t('profile.Deposit') : $t('profile.Withdrawal') }}
          </div>
        </td>
        <td class="amount">{{ formatAmount(transaction.amount) }}</td>
        <td>
          <span :class="['status-badge', getStatusClass(transaction.status || transaction.state)]">
            {{ getStatusText(transaction.status || transaction.state) }}
          </span>
        </td>
        <td class="date">{{ formatDate(transaction.createdAt) }}</td>
      </tr>
    </tbody>
  </table>
</div>

<!-- Mobil Kart Görünümü -->
<div v-if="!loading && filteredTransactions.length > 0" class="mobile-cards">
  <div v-for="(transaction, index) in paginatedTransactions" :key="index" class="card">
    <div class="card-header">
      <div class="transaction-type">
        <span class="type-icon" :class="transaction.type">
          {{ transaction.type === 'deposit' ? '↓' : '↑' }}
        </span>
        {{ transaction.type === 'deposit' ? $t('profile.Deposit') : $t('profile.Withdrawal') }}
      </div>
      <span :class="['status-badge', getStatusClass(transaction.status || transaction.state)]">
        {{ getStatusText(transaction.status || transaction.state) }}
      </span>
    </div>
    <div class="card-content">
      <div class="card-row">
        <span class="label">{{ $t('profile.Amount') }}:</span>
        <span class="value amount">{{ formatAmount(transaction.amount) }}</span>
      </div>
      <div class="card-row">
        <span class="label">{{ $t('profile.Date') }}:</span>
        <span class="value date">{{ formatDate(transaction.createdAt) }}</span>
      </div>
    </div>
  </div>
</div>


    <!-- Sayfalandırma -->
    <div v-if="!loading && filteredTransactions.length > 0" class="pagination-container">
      <div class="pagination-info">
        {{ $t('profile.shown') }} {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage, filteredTransactions.length) }} {{ $t('profile.total') }} {{ filteredTransactions.length }} {{ $t('profile.from the record') }}
      </div>
      <div class="pagination">
        <button 
          @click="prevPage" 
          :disabled="currentPage === 1" 
          class="pagination-btn"
          aria-label="Önceki sayfa"
        >
          &lt;
        </button>
        
        <div class="page-numbers">
          <button 
            v-for="page in visiblePages" 
            :key="page"
            @click="goToPage(page)"
            :class="{ active: currentPage === page }"
            class="page-btn"
          >
            {{ page }}
          </button>
          <span v-if="showEllipsis" class="ellipsis">...</span>
        </div>
        
        <button 
          @click="nextPage" 
          :disabled="currentPage === totalPages" 
          class="pagination-btn"
          aria-label="Sonraki sayfa"
        >
          &gt;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';

export default {
  data() {
    return {
      transactions: [],
      activeFilter: 'deposit',
      currentPage: 1,
      itemsPerPage: 15,
      loading: true,
      maxVisiblePages: 5
    };
  },
  computed: {
    ...mapGetters(['authUser']),
    
    filteredTransactions() {
      if (!this.transactions) return [];
      return this.transactions.filter(
        (transaction) => {
          const type = transaction.type === 'withdraw' ? 'withdrawal' : transaction.type;
          return type === this.activeFilter;
        }
      );
    },
    
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredTransactions.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredTransactions.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const half = Math.floor(this.maxVisiblePages / 2);
      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + this.maxVisiblePages - 1);
      
      if (end - start < this.maxVisiblePages - 1) {
        start = Math.max(1, end - this.maxVisiblePages + 1);
      }
      
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    showEllipsis() {
      return this.totalPages > this.maxVisiblePages && 
             Math.max(...this.visiblePages) < this.totalPages;
    }
  },
  watch: {
    activeFilter() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchTransactionHistory();
  },
  methods: {
    async fetchTransactionHistory() {
      try {
        this.loading = true;
        const userIdentifier = this.authUser.user._id || this.authUser.user.phone;
        const response = await axios.get(`/transaction-history/${userIdentifier}`);
        this.transactions = response.data.transactions || [];
      } catch (error) {
        console.error('İşlem geçmişi çekilirken hata oluştu:', error);
        this.$toast.error('İşlem geçmişi yüklenirken bir hata oluştu');
      } finally {
        this.loading = false;
      }
    },

    formatAmount(amount) {
  if (!amount && amount !== 0) return '';
  return Number(amount).toFixed(2);
},

    
    setFilter(filter) {
      this.activeFilter = filter;
    },
    
    getStatusClass(status) {
      const statusLower = status.toLowerCase();
      if (statusLower.includes('pending') || statusLower.includes('beklemede')) {
        return 'status-pending';
      } else if (statusLower.includes('completed') || statusLower.includes('tamamlandı')) {
        return 'status-completed';
      } else if (statusLower.includes('rejected') || statusLower.includes('reddedildi')) {
        return 'status-rejected';
      }
      return '';
    },
    
    getStatusText(status) {
      const statusLower = status.toLowerCase();
      if (statusLower.includes('pending') || statusLower.includes('beklemede')) {
        return this.$t('profile.Pending');
      } else if (statusLower.includes('completed') || statusLower.includes('tamamlandı')) {
        return this.$t('profile.Succes');
      } else if (statusLower.includes('rejected') || statusLower.includes('reddedildi')) {
        return this.$t('profile.Rejected');
      }
      return status;
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    }
  },
};
</script>

<style scoped>
.transaction-history-container {

  
  color: #fff;
 
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-header h2 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: #FFD700;
}

.header-actions {
  display: flex;
  align-items: center;
}

.items-per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--background2);
  padding: 8px 12px;
  border-radius: 8px;
}

.items-per-page-selector select {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  padding: 4px 8px;
  cursor: pointer;
}

.filter-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.filter-buttons {
  display: flex;
  gap: 12px;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: var(--background2);
  color: #fff;
  font-weight: 600;
  transition: all 0.3s ease;
}

.filter-btn:hover {
  background: rgba(0, 255, 194, 0.1);
}

.filter-btn.active {
  background: var(--active-button);
  color: #fff;

}

.btn-icon {
  font-weight: bold;
}

.results-info {
  display: flex;
  align-items: center;
}

.results-count {
  font-size: 0.9rem;
  color: #aaa;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-left: 4px solid #01e0a3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #888;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  color: #ddd;
}

.empty-state p {
  margin: 0;
  font-size: 0.95rem;
}

.table-container {
  overflow-x: auto;
  border-radius: 8px;
  margin: 20px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.transaction-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--background2);
  border-radius: 8px;
  overflow: hidden;
}

.transaction-table th {
  background-color: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-weight: 600;
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.transaction-table td {
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.transaction-table tbody tr:last-child td {
  border-bottom: none;
}

.transaction-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.transaction-type {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-weight: bold;
}

.type-icon.deposit {
  background: rgba(0, 255, 194, 0.15);
  color: #01e0a3;
}

.type-icon.withdraw, .type-icon.withdrawal {
  background: rgba(255, 82, 82, 0.15);
  color: #ff5252;
}

.amount {
  font-weight: 600;
  color: #fff;
}

.date {
  color: #aaa;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: capitalize;
  display: inline-block;
}

.status-pending {
  background: rgba(255, 204, 0, 0.15);
  color: #ffcc00;
}

.status-completed {
  background: rgba(0, 204, 102, 0.15);
  color: #00cc66;
}

.status-rejected {
  background: rgba(255, 51, 51, 0.15);
  color: #ff3333;
}

.mobile-cards {
  display: none;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0;
}

.card {
  background: var(--background2);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-row {
  display: flex;
  justify-content: space-between;
}

.card-row .label {
  font-weight: 600;
  color: #FFD700;
}

.card-row .value {
  font-weight: 500;
  color: #fff;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.pagination-info {
  font-size: 0.9rem;
  color: #aaa;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn, .page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  border: none;
  border-radius: 8px;
  background: var(--background2);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled), .page-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.1);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn.active {
  background: var(--button);
  color: #000;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ellipsis {
  padding: 0 8px;
  color: #888;
}

@media (max-width: 768px) {
  .transaction-history-container {
    padding: 16px;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .filter-section {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .filter-buttons {
    width: 100%;
  }
  
  .filter-btn {
    flex: 1;
    justify-content: center;
  }
  
  .desktop-table {
    display: none;
  }
  
  .mobile-cards {
    display: flex;
  }
  
  .pagination-container {
    flex-direction: column;
    align-items: center;
  }
  
  .pagination-info {
    order: 2;
  }
  
  .pagination {
    order: 1;
  }
}

@media (max-width: 480px) {
  .page-numbers {
    display: none;
  }
  
  .pagination-info {
    text-align: center;
  }
}
</style>